import React, { useState, useEffect, useRef } from 'react';
import { CATEGORIES, COMPANY_DIVISIONS, CompanySector } from '../data';
import { Database, Hexagon, Component, Play, Cpu, CheckCircle2, Terminal, ArrowRight, Mail, Server, Shield, Coins, Sparkles, Mic, Zap, Search, ChevronDown, ChevronUp } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { HermesInstance } from '../lib/NexoraxHermesAgent';
import { useSiteContent } from '../contexts/SiteContentContext';
import { useAuth } from '../lib/AuthContext';

interface HolographicDatabaseProps {
  initialCategoryId?: number;
  onSelectCategory?: (title: string) => void;
  onShowArchitectVoice?: () => void;
}

export function HolographicDatabase({ initialCategoryId, onSelectCategory, onShowArchitectVoice }: HolographicDatabaseProps = {}) {
  const [activeCategoryId, setActiveCategoryId] = useState<number>(initialCategoryId || -1); // Default to -1 (Company Divisions) for maximum priority visibility
  const [dbDivSearch, setDbDivSearch] = useState('');
  const [dbExpandedSectors, setDbExpandedSectors] = useState<Record<string, boolean>>({});

  const dbItems = [{ id: -1, title: 'Company Divisions' }, ...CATEGORIES];
  const [highlightedDbIndex, setHighlightedDbIndex] = useState(0);
  const databaseScrollRef = useRef<HTMLDivElement>(null);

  const { companyDivisions } = useAuth();
  const { content } = useSiteContent();
  const dbCompanySectors = companyDivisions && companyDivisions.length > 0 ? companyDivisions : (content.companyDivisions || COMPANY_DIVISIONS);

  useEffect(() => {
    if (initialCategoryId) {
      setActiveCategoryId(initialCategoryId);
    }
  }, [initialCategoryId]);

  // Synchronize active category highlighted index on category update
  useEffect(() => {
    const idx = dbItems.findIndex(item => item.id === activeCategoryId);
    if (idx !== -1) {
      setHighlightedDbIndex(idx);
    }
  }, [activeCategoryId]);

  // Handle automatic scrolling to keep highlighted database category in view
  useEffect(() => {
    if (!databaseScrollRef.current) return;
    const highlightedEl = databaseScrollRef.current.querySelector('[data-highlighted-db="true"]') as HTMLElement;
    if (highlightedEl) {
      highlightedEl.scrollIntoView({ block: 'nearest' });
    }
  }, [highlightedDbIndex]);

  // Listen for keyboard arrow keys and trigger category selection state
  useEffect(() => {
    const handleGlobalDbKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement;
      const isInput = target.tagName === 'INPUT' || target.tagName === 'SELECT' || target.tagName === 'TEXTAREA';
      if (isInput) return;

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setHighlightedDbIndex(prev => (prev >= dbItems.length - 1 ? 0 : prev + 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setHighlightedDbIndex(prev => (prev <= 0 ? dbItems.length - 1 : prev - 1));
      } else if (e.key === 'Enter') {
        e.preventDefault();
        const targetItem = dbItems[highlightedDbIndex];
        if (targetItem) {
          setActiveCategoryId(targetItem.id);
          if (onSelectCategory) {
            onSelectCategory(targetItem.title);
          }
        }
      }
    };

    window.addEventListener('keydown', handleGlobalDbKeyDown);
    return () => window.removeEventListener('keydown', handleGlobalDbKeyDown);
  }, [highlightedDbIndex, onSelectCategory, dbItems]);

  const activeCategory = CATEGORIES.find(c => c.id === activeCategoryId);

  // Hermes Simulation States
  const [hermesLogs, setHermesLogs] = useState<string[]>([]);
  const [isSimulating, setIsSimulating] = useState(false);
  const [simulationStep, setSimulationStep] = useState(0);
  const [processedProspects, setProcessedProspects] = useState<any[]>([]);
  const logsEndRef = useRef<HTMLDivElement>(null);

  // Live Outreach & Reconciliation state
  const [reconcileAmount, setReconcileAmount] = useState<number>(24000);
  const [reconcileMethod, setReconcileMethod] = useState<'CARD' | 'BANK_TRANSFER'>('BANK_TRANSFER');
  const [reconcileResult, setReconcileResult] = useState<any>(
    HermesInstance.reconcileRevenue('SecurePayment Stream', 24000, 'BANK_TRANSFER')
  );

  // Category 84 (NexoraX Engine) States
  const [engineInput, setEngineInput] = useState<number>(120000);
  const [engineLoading, setEngineLoading] = useState(false);
  const [engineLogs, setEngineLogs] = useState<string[]>([]);
  const [engineStatus, setEngineStatus] = useState<string>("IDLE");

  // Category 85 (NexoraX Generator) States
  const [generatorPrompt, setGeneratorPrompt] = useState<string>("");
  const [selectedGeneratorNode, setSelectedGeneratorNode] = useState<string>("Content Swarm Generator");
  const [generatorLoading, setGeneratorLoading] = useState(false);
  const [generatedResult, setGeneratedResult] = useState<any>(null);

  // Calculation parameters for Category 84
  const netAmount = Number(engineInput) || 0;
  const vatAmount = netAmount * 0.20;
  const grossAmount = netAmount + vatAmount;
  const levyAmount = netAmount * 0.01;
  const lifetimeTax = netAmount * 0.20;
  const netCorporateYield = netAmount - levyAmount - lifetimeTax;
  
  const charity1Split = levyAmount * (1/3); // Dunfermline (33.33%)
  const charity2Split = levyAmount * (1/3); // Global (33.33%)
  const charity3Split = levyAmount * (1/3); // UK (33.33%)

  const executeEngineReconciliation = () => {
    if (engineLoading) return;
    setEngineLoading(true);
    setEngineStatus("EXECUTING");
    setEngineLogs([]);

    const eventLogs = [
      `[NODE-84-SYNAPSE] Initializing Cognitive Decision Pathway with input balance £${netAmount.toLocaleString()}...`,
      `[COMPLIANCE] Triggering Nexorax VAT Billing Protocol (V.NexoraX+)...`,
      `[CALCULATION] Core Net Invoice: £${netAmount.toLocaleString()} | 20% VAT: £${vatAmount.toLocaleString()} | Total Client Gross Invoice: £${grossAmount.toLocaleString()}`,
      `[TAXATION] 10% Maintenance & Hosting Fee (10% Maintenance & Hosting Fee) captured: £${lifetimeTax.toLocaleString()} to Chronos Ledger`,
      `[3% Community Allocation] 1% Perpetual Levy: £${levyAmount.toLocaleString()} extracted instantly`,
      `[CHARITY DISTRIBUTION] Charity 1 (Starting Dunfermline): £${charity1Split.toFixed(2)} [ACTIVE]`,
      `[CHARITY DISTRIBUTION] Charity 2 (Global): £${charity2Split.toFixed(2)} [ACTIVE]`,
      `[CHARITY DISTRIBUTION] Charity 3 (UK): £${charity3Split.toFixed(2)} [ACTIVE]`,
      `[SETTLEMENT] Routing Net finance Balance (£${netCorporateYield.toLocaleString()}) to Secure Payment Provider...`,
      `[BANK PROTOCOL] Bank Target: SecurePayment (Sort Code: Verified Payment Configuration | Account Number: Secure Billing Provider)`,
      `[AUTHENTICATION] Signatures cross-validated with CloudSync-02 proxy`,
      `[MIRRORING] Real-time replica state pushed securely to Google Drive NexoraX Resource Library`,
      `[COMPLETED] 0.8s SLA Met. Ledger locked completely with Zero-Entropy consensus.`
    ];

    let logIdx = 0;
    const timer = setInterval(() => {
      if (logIdx < eventLogs.length) {
        setEngineLogs(prev => [...prev, eventLogs[logIdx]]);
        logIdx++;
      } else {
        clearInterval(timer);
        setEngineLoading(false);
        setEngineStatus("COMPLETED");
      }
    }, 180);
  };

  const executeGeneratorAssetForge = () => {
    if (generatorLoading) return;
    setGeneratorLoading(true);
    setGeneratedResult(null);

    const promptsMap: { [key: string]: { description: string, text?: string } } = {
      "Content Swarm Generator": {
        description: "A swarm of automated content variations deployed to digital frontlines completely pre-optimized for local search domination.",
        text: `### CAMPAIGN PACK BRAND ARCHETYPE: NEXORAX FIEFDOM\n\n- **Anchor Visual**: High-contrast modern sans-serif display type layered over deep titanium black brushed metal canvas with emerald status pings.\n- **Launch Tagline**: 'Immediate Transition to NexoraX Infrastructure v3000.'\n- **Key Pitch Segment**: 'SMEs across Scotland are facing critical, unneeded revenue leaks. We patch this in 0.8s with native zero-friction SecurePayment settlement routing.'`
      },
      "Synthetic Art Engine": {
        description: "A gorgeous, modern generative visual card showing the NexoraX NexoraX Grid structure, utilizing beautiful color psychology gradients.",
        text: "--- GENERATIVE SYSTEM ARCHITECTURE DIAGRAM ---\n[NODE-85-ART-SYNTHESIS] Gradient: #05050d -> #240046 -> #0f0f15\nAccents: Hexagonal Glowing Vectors in #e840f3 Vibrant Violet & #10b981 NexoraX Emerald.\nWatermark: 'NexoraX Digital SC887095 - Guardian Protected'"
      },
      "Automated Marketing Copywriter": {
        description: "High-conversion outreach templates optimized for instant messaging, copy-pastable with built-in financial hooks.",
        text: `### CORE OUTREACH MULTIVERSE DIRECTIVE:\n\n"Hello [Target], \n\nI am initializing the NexoraX Digital deployment for our enterprise partners this week. We have identified multiple digital structure gaps within your pipeline. We have a priority onboarding slot available today. Please clear the activation invoice via the secure link below to proceed:\nhttps://buy.stripe.com/8x2bIU2t59oYgTtfxQaMU00\nAll net billing settlements are routed live to Secure Payment Provider."`
      }
    };

    setTimeout(() => {
      setGeneratorLoading(false);
      const defaultChoice = promptsMap[selectedGeneratorNode] || {
        description: `Synthesized digital asset output tailored precisely utilizing standard ${selectedGeneratorNode} capabilities.`,
        text: `[ASSET FORGE COMPLETE]\n\nType: ${selectedGeneratorNode}\nTarget SLA: 0.8s achieved\nFormat: Vector Node Protocol / High-Fidelity Array\nStatus: Securely Mirrored to NexoraX Resource Library via CloudSync-02.`
      };
      
      setGeneratedResult(defaultChoice);
    }, 1500);
  };


  useEffect(() => {
    if (logsEndRef.current) {
      logsEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [hermesLogs]);

  const runHermesOutreach = async () => {
    if (isSimulating) return;
    setIsSimulating(true);
    setHermesLogs([]);
    setProcessedProspects([]);
    setSimulationStep(1);

    // Call factual outreach scanning method
    const prospects = await HermesInstance.runAutomatedScan(["Fife", "Dunfermline"]);

    const logList = [
      "[SYSTEM] Initializing Nex Agent stack on hyper-node v5.161.42.190...",
      `[AUTH] Credentials verified: Lead Director (Director, NexoraX Digital)`,
      `[SECURITY] Mirroring active via CloudSync-02 to NexoraX Resource Library...`,
      `[SCAN] Running active scan for local directory prospects...`,
      `[NODE-81] Executing runAutomatedScan on compiled NexoraxHermesAgent...`,
      `[DETECTION] Verified ${prospects.length} high-potential local targets with critical digital gaps:`
    ];

    let logIndex = 0;
    const interval = setInterval(() => {
      if (logIndex < logList.length) {
        setHermesLogs(prev => [...prev, logList[logIndex]]);
        logIndex++;
      } else {
        clearInterval(interval);
        simulateTargets(prospects);
      }
    }, 400);
  };

  const simulateTargets = (prospects: string[]) => {
    const defaultData = [
      { email: "procurement@kingdombakers.co.uk", loss: "£14,200/yr leak" },
      { email: "info@dunfermline-seafoods.com", loss: "£22,500/yr leak" },
      { email: "admin@fifeconstruction.co.uk", loss: "£48,000/yr leak" },
      { email: "partner@deesidelogistics.co.uk", loss: "£31,000/yr leak" }
    ];

    let targetIndex = 0;
    const interval = setInterval(() => {
      if (targetIndex < prospects.length) {
        setSimulationStep(2);
        const name = prospects[targetIndex];
        const info = defaultData[targetIndex] || { email: "outreach@nexoraxdigital.co.uk", loss: "£15,000/yr leak" };
        
        setHermesLogs(prev => [
          ...prev, 
          `[TARGET DETECTED] ${name} (${info.email}) • Risk level: SEVERE • Estimated leakage: ${info.loss}`,
          `[PROPOSAL] Drafting NexoraX Node-3000 transition proposal for ${name}...`,
          `[PROPOSAL] Hydrating parameters: Direct Secure Payment Provider Transfer discount (5% incentive mapped).`,
          `[RELAY] Direct dispatch routed through Category 64 Secure Mail Relay...`,
          `[SMTP] SMTP Handshake verified with ${info.email}. Outreach packet sent.`
        ]);

        setProcessedProspects(prev => [...prev, { name, email: info.email, loss: info.loss, status: "DISPATCHED" }]);
        targetIndex++;
      } else {
        clearInterval(interval);
        finalizeSimulation();
      }
    }, 800);
  };

  const finalizeSimulation = () => {
    setSimulationStep(3);
    setHermesLogs(prev => [
      ...prev,
      "[SYSTEM] Scanning cycles completed.",
      "[LEDGER] Verified transactional state synchronized under Chronos Ledger.",
      "[COMPLETED] Hermes outreach sequence completed. 0.8s SLA met perfectly. Active Nodes stable."
    ]);
    setIsSimulating(false);
  };

  return (
    <div className="flex flex-col h-full border border-indigo-500/20 rounded-xl glass-panel overflow-hidden relative bg-[#050505]">
      {/* Background Decor */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-1/2 left-1/4 w-[500px] h-[500px] bg-indigo-900/10 rounded-full blur-[100px] transform -translate-x-1/2 -translate-y-1/2"></div>
        <div className="absolute bottom-0 right-1/4 w-[400px] h-[400px] bg-cyan-900/10 rounded-full blur-[80px]"></div>
      </div>

      {/* Categories Sidebar (Now top list) */}
      <div className="border-b border-indigo-500/20 bg-[#000000]/40 flex flex-col z-10 h-[30%] lg:h-[40%] shrink-0">
        <div className="p-3 border-b border-indigo-500/20 flex items-center gap-2 frosted-bg shrink-0">
          <Database className="text-indigo-400 shrink-0" size={14} />
          <h3 className="font-mono text-[10px] tracking-[0.2em] font-bold text-slate-200 truncate">NEXORAX CATEGORY DATABASE</h3>
        </div>
        <div 
          ref={databaseScrollRef}
          className="flex-1 overflow-y-auto custom-scrollbar p-2 space-y-1"
        >
          {/* SPECIAL PRIORITY: COMPANY DIVISIONS */}
          {(() => {
            const isHighlighted = highlightedDbIndex === 0;
            return (
              <button
                key="divisions-category-btn"
                onClick={() => {
                  setActiveCategoryId(-1);
                  if (onSelectCategory) {
                    onSelectCategory("Company Divisions");
                  }
                }}
                data-highlighted-db={isHighlighted}
                className={`w-full text-left px-3 py-2.5 rounded-md font-sans text-xs tracking-wide transition-all duration-300 flex items-center justify-between border-l-2 leading-normal ${
                  activeCategoryId === -1
                    ? 'bg-cyan-500/15 border-cyan-400 text-cyan-300 shadow-[inset_0_0_15px_rgba(0,195,255,0.05)]'
                    : isHighlighted
                      ? 'bg-white/10 text-zinc-100 border-indigo-400/50'
                      : 'text-zinc-400 border-transparent hover:bg-white/5 hover:text-zinc-200'
                }`}
              >
                <span className="truncate pr-2 flex items-center gap-1 font-medium">
                  <span className="font-mono text-[10px] text-cyan-400/80 mr-2 font-bold select-none">00</span>
                  <span className="text-cyan-400 font-extrabold select-none">●</span>
                  <span>Company Divisions</span>
                </span>
                {activeCategoryId === -1 && (
                  <motion.div layoutId="activeInd" className="w-1.5 h-1.5 rounded-full bg-cyan-400 shadow-[0_0_5px_#00c3ff] shrink-0" />
                )}
              </button>
            );
          })()}

          {CATEGORIES.map((cat, index) => {
            const isHighlighted = highlightedDbIndex === index + 1;
            const cleanTitle = cat.title.replace(/^CATEGORY \d+:\s*/i, '').replace(/\s*\(NODE-\d+\).*$/i, '').trim().toLowerCase();
            return (
              <button
                key={cat.id}
                onClick={() => {
                  setActiveCategoryId(cat.id);
                  if (onSelectCategory) {
                    onSelectCategory(cat.title);
                  }
                }}
                data-highlighted-db={isHighlighted}
                className={`w-full text-left px-3 py-2.5 rounded-md font-sans text-xs tracking-wide transition-all duration-300 flex items-center justify-between border-l-2 leading-normal ${
                  activeCategoryId === cat.id
                    ? 'bg-indigo-500/15 border-indigo-500 text-indigo-300 shadow-[inset_0_0_15px_rgba(112,0,255,0.05)]'
                    : isHighlighted
                      ? 'bg-white/10 text-zinc-100 border-indigo-400/50'
                      : 'text-zinc-400 border-transparent hover:bg-white/5 hover:text-zinc-200'
                }`}
              >
                <span className="truncate pr-2 flex items-center gap-1 font-medium">
                  <span className="font-mono text-[10px] text-indigo-400/80 mr-2 font-bold">#{cat.id.toString().padStart(2, '0')}</span>
                  <span className="capitalize">{cleanTitle}</span>
                </span>
                {activeCategoryId === cat.id && (
                  <motion.div layoutId="activeInd" className="w-1.5 h-1.5 rounded-full bg-indigo-400 shadow-[0_0_5px_#7000FF] shrink-0" />
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Nodes Display Area (Now bottom detail) */}
      <div className="flex-1 p-4 lg:p-6 z-10 flex flex-col relative overflow-y-auto custom-scrollbar bg-black/20">
        <AnimatePresence mode="wait">
          {activeCategoryId === -1 ? (
            <motion.div
              key="company-divisions-detail"
              initial={{ opacity: 0, y: 10, filter: 'blur(5px)' }}
              animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
              exit={{ opacity: 0, y: -10, filter: 'blur(5px)' }}
              transition={{ duration: 0.3 }}
              className="flex-1 flex flex-col h-full space-y-6"
            >
              <div className="mb-2">
                <div className="flex items-center gap-4 mb-2">
                  <div className="text-3xl font-mono text-cyan-500/40 font-light">
                    00
                  </div>
                  <h2 className="text-2xl font-bold font-sans tracking-tight text-[#e2e8f0] glow-text-cyan flex items-center gap-3">
                    COMPANY DIVISIONS DIRECTORY
                  </h2>
                </div>
                <div className="mt-2 text-xs font-mono text-cyan-400 bg-cyan-950/20 border border-cyan-500/20 px-3 py-2 rounded-md tracking-wider flex items-center gap-2">
                  <span className="text-indigo-400 font-extrabold uppercase">MANDATED GRID:</span>
                  <span>NexoraX Digital Ltd (SC887095) Enterprise Registry</span>
                </div>
                <div className="h-px w-full bg-gradient-to-r from-cyan-500/50 to-transparent mt-4" />
              </div>

              {/* SEARCH SYSTEM FOR SIDEBAR VIEW */}
              <div className="relative group w-full mb-2">
                <div className="relative w-full bg-[#03060c] ring-1 ring-slate-800 focus-within:ring-cyan-500/50 rounded-xl leading-none flex items-center">
                  <div className="p-3">
                    <Search className="w-3.5 h-3.5 text-cyan-400" />
                  </div>
                  <input 
                    type="text" 
                    value={dbDivSearch}
                    onChange={(e) => setDbDivSearch(e.target.value)}
                    placeholder="Search company sectors..."
                    className="w-full bg-transparent text-slate-100 font-mono placeholder-slate-700 focus:outline-none text-[10px] py-2"
                  />
                </div>
              </div>

              {/* DYNAMIC COMPANY DIVISIONS CONTAINER */}
              <div className="grid grid-cols-1 gap-4 max-h-[35rem] overflow-y-auto pr-1 select-none custom-scrollbar pb-6">
                {dbCompanySectors.filter(sec => 
                  !dbDivSearch ||
                  sec.title.toLowerCase().includes(dbDivSearch.toLowerCase()) ||
                  sec.node.toLowerCase().includes(dbDivSearch.toLowerCase()) ||
                  sec.description.toLowerCase().includes(dbDivSearch.toLowerCase()) ||
                  sec.owner.toLowerCase().includes(dbDivSearch.toLowerCase())
                ).map((sec) => {
                  const getIconElement = (name: string, colorClass: string) => {
                    switch (name) {
                      case 'Terminal': return <Terminal className={`w-4 h-4 ${colorClass}`} />;
                      case 'Cpu': return <Cpu className={`w-4 h-4 ${colorClass}`} />;
                      case 'Database': return <Database className={`w-4 h-4 ${colorClass}`} />;
                      case 'Mic': return <Mic className={`w-4 h-4 ${colorClass}`} />;
                      case 'Shield': return <Shield className={`w-4 h-4 ${colorClass}`} />;
                      case 'Zap': return <Zap className={`w-4 h-4 ${colorClass}`} />;
                      default: return <Database className={`w-4 h-4 ${colorClass}`} />;
                    }
                  };
                  const colorClass = (sec.color && typeof sec.color === 'string' ? sec.color.split(' ')[1] : '') || 'text-[#e840f3]';
                  const isExpanded = dbExpandedSectors[sec.id] || false;

                  return (
                    <div
                      key={sec.id}
                      onClick={() => {
                        setDbExpandedSectors(prev => ({
                          ...prev,
                          [sec.id]: !prev[sec.id]
                        }));
                        if (onSelectCategory) {
                          if (sec.id === 'sec-1') {
                            onSelectCategory("SECRETARY NODE");
                          } else if (sec.id === 'sec-2') {
                            onSelectCategory("NODE 0: ARCHITECT TERMINAL");
                          } else if (sec.id === 'sec-3') {
                            onSelectCategory("REALITY AUDIT LEDGER");
                          } else if (sec.id === 'sec-4') {
                            onSelectCategory("NODE 0: ARCHITECT TERMINAL");
                            onShowArchitectVoice && onShowArchitectVoice();
                          } else if (sec.id === 'sec-5') {
                            onSelectCategory("SECRETARY NODE");
                          } else if (sec.id === 'sec-6') {
                            onSelectCategory("CRM, DASHBOARD & SLIDE DECK");
                          }
                        }
                      }}
                      className={`p-4 bg-black/75 border transition-all duration-300 rounded-2xl space-y-3 cursor-pointer ${
                        isExpanded
                          ? 'border-cyan-500/80 shadow-[0_0_20px_rgba(6,182,212,0.1)] ring-1 ring-cyan-500/20'
                          : 'border-slate-800 hover:border-slate-700'
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="space-y-0.5 max-w-[80%]">
                          <span className="text-[7.5px] font-mono font-black text-slate-500 uppercase tracking-wider block flex items-center gap-1.5">
                            <span className={`w-1 h-1 rounded-full ${isExpanded ? 'bg-cyan-400 animate-pulse' : 'bg-slate-600'}`}></span>
                            {sec.node}
                          </span>
                          <h4 className="text-xs font-black text-white uppercase tracking-wider">
                            {sec.title}
                          </h4>
                        </div>
                        <div className="flex items-center gap-1.5 shrink-0">
                          <div className={`p-2 bg-slate-900/50 border rounded-lg shrink-0 ${isExpanded ? 'border-cyan-500 text-cyan-400' : 'border-slate-800'}`}>
                            {getIconElement(sec.iconName, colorClass)}
                          </div>
                          {isExpanded ? (
                            <ChevronUp className="w-3.5 h-3.5 text-cyan-400" />
                          ) : (
                            <ChevronDown className="w-3.5 h-3.5 text-slate-500" />
                          )}
                        </div>
                      </div>

                      {isExpanded ? (
                        <div className="space-y-3 pt-2 border-t border-slate-900/60 overflow-hidden">
                          <p className="text-[10px] text-slate-400 font-sans leading-relaxed text-left font-medium">
                            {sec.description}
                          </p>

                          <div className="pt-2 border-t border-slate-900 flex flex-col gap-1.5 uppercase font-[#e840f3] text-left">
                            <div className="text-[7px] text-[#e840f3] font-bold">
                              OWNER: {sec.owner}
                            </div>
                            <div className="text-[8px] text-cyan-400/80 font-black tracking-wider font-mono">
                              METRIC: {sec.metric}
                            </div>
                          </div>

                          {/* Navigation handler button */}
                          <div className="pt-2 border-t border-slate-900/40 flex justify-end">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                if (onSelectCategory) {
                                  if (sec.id === 'sec-1') {
                                    onSelectCategory("SECRETARY NODE");
                                  } else if (sec.id === 'sec-2') {
                                    onSelectCategory("NODE 0: ARCHITECT TERMINAL");
                                  } else if (sec.id === 'sec-3') {
                                    onSelectCategory("REALITY AUDIT LEDGER");
                                  } else if (sec.id === 'sec-4') {
                                    onSelectCategory("NODE 0: ARCHITECT TERMINAL");
                                    onShowArchitectVoice && onShowArchitectVoice();
                                  } else if (sec.id === 'sec-5') {
                                    onSelectCategory("SECRETARY NODE");
                                  } else if (sec.id === 'sec-6') {
                                    onSelectCategory("CRM, DASHBOARD & SLIDE DECK");
                                  }
                                }
                              }}
                              className="text-[8px] font-mono font-bold uppercase select-none px-3 py-1.5 bg-cyan-950/60 border border-cyan-500/80 text-cyan-400 hover:bg-cyan-900/40 hover:text-white rounded-md cursor-pointer transition-all active:scale-95"
                            >
                              [ LAUNCH NODE ]
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="text-[7.5px] font-mono text-slate-600 font-bold uppercase tracking-widest text-left pl-1">
                          [ CLICK TO REVEAL INTEL CORE ]
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </motion.div>
          ) : (
            activeCategory && (
              <motion.div
                key={activeCategory.id}
                initial={{ opacity: 0, y: 10, filter: 'blur(5px)' }}
                animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
                exit={{ opacity: 0, y: -10, filter: 'blur(5px)' }}
                transition={{ duration: 0.3 }}
                className="flex-1 flex flex-col h-full"
              >
              <div className="mb-6">
                <div className="flex items-center gap-4 mb-2">
                  <div className="text-3xl font-mono text-indigo-500/40 font-light">
                    {activeCategory.id.toString().padStart(2, '0')}
                  </div>
                  <h2 className="text-2xl font-bold font-sans tracking-tight text-[#e2e8f0] glow-text-cyan flex items-center gap-3">
                    {activeCategory.title}
                  </h2>
                </div>
                {activeCategory.protocol && (
                  <div className="mt-2 text-xs font-mono text-cyan-400 bg-cyan-950/20 border border-cyan-500/20 px-3 py-2 rounded-md tracking-wider flex items-center gap-2">
                    <span className="text-indigo-400 font-extrabold uppercase">Protocol:</span>
                    <span>{activeCategory.protocol}</span>
                  </div>
                )}
                <div className="h-px w-full bg-gradient-to-r from-indigo-500/50 to-transparent mt-4" />
              </div>

              {/* SPECIAL INTERACTIVE DECK FOR CATEGORY 81 (HERMES GRID) AND CATEGORY 56 */}
              {activeCategory.id === 56 || activeCategory.id === 81 ? (
                <div className="space-y-6">
                  <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
                    
                    {/* Simulation deployment Control Panel */}
                    <div className="bg-[#030b14]/90 border border-slate-800 rounded-xl p-5 flex flex-col justify-between shadow-[0_0_40px_rgba(112,0,255,0.05)] relative overflow-hidden group">
                      <div className="absolute top-0 right-0 w-32 h-32 bg-indigo-500/5 blur-[50px] pointer-events-none"></div>
                      
                      <div>
                        <div className="flex items-center justify-between mb-4 pb-2 border-b border-slate-900">
                          <div className="flex items-center gap-2">
                            <Server className="w-4 h-4 text-cyan-400" />
                            <span className="font-mono text-xs text-white font-bold tracking-widest">DEPLOYMENT NODE: 5.161.42.190</span>
                          </div>
                          <span className="px-2 py-0.5 rounded text-[8px] font-mono text-cyan-300 bg-cyan-950/30 border border-cyan-500/20 font-bold uppercase">HERMES-v1.0</span>
                        </div>
                        
                        <p className="text-xs text-slate-400 leading-relaxed mb-6">
                          Autonomous prospect extraction. Scans local Dunfermline &amp; Fife targets for structural digital starvation gaps, converts values into direct checkout pitches, and executes Secure SMTP relais using authentic UK Compliance standards.
                        </p>

                        <div className="space-y-3 mb-6">
                          <div className="flex justify-between items-center text-xs font-mono border-b border-slate-900 pb-2">
                            <span className="text-slate-500">TARGET MARKET:</span>
                            <span className="text-white font-bold tracking-wider">FIFE_SME_SECTOR [Fife / Dunfermline]</span>
                          </div>
                          <div className="flex justify-between items-center text-xs font-mono border-b border-slate-900 pb-2">
                            <span className="text-slate-500">LEVY ESCROW ROUTE:</span>
                            <span className="text-emerald-400">1% PERPETUAL ZAKAT</span>
                          </div>
                          <div className="flex justify-between items-center text-xs font-mono pb-2">
                            <span className="text-slate-500">SETTLEMENT DISPATCH:</span>
                            <span className="text-cyan-400">SecurePayment [Verified Payment Configuration | Secure Billing Provider] - 5% INCENTIVE</span>
                          </div>
                        </div>
                      </div>

                      <div className="space-y-3">
                        <button
                          onClick={runHermesOutreach}
                          disabled={isSimulating}
                          className={`w-full py-3 rounded-lg font-mono text-xs uppercase tracking-widest font-bold flex items-center justify-center gap-2 border transition-all ${
                            isSimulating
                              ? "bg-slate-950/40 border-slate-800 text-slate-600 cursor-not-allowed"
                              : "bg-gradient-to-r from-indigo-700 to-indigo-900 hover:from-indigo-600 hover:to-indigo-800 text-white border-indigo-500/30 hover:shadow-[0_0_20px_rgba(99,102,241,0.35)] cursor-pointer"
                          }`}
                        >
                          <Play className="w-4 h-4" /> {isSimulating ? "AGENT RUNNING..." : "DEPLOY & EXECUTE REVENUE STRIKE"}
                        </button>
                      </div>
                    </div>

                    {/* Node-81 Factual Revenue Reconciler Box */}
                    <div className="bg-[#010409] border border-slate-800 rounded-xl p-5 flex flex-col justify-between shadow-2xl relative">
                      <div className="absolute top-0 right-0 w-24 h-24 bg-emerald-500/5 blur-3xl pointer-events-none"></div>

                      <div>
                        <div className="flex items-center justify-between pb-2 border-b border-slate-900 mb-4">
                          <div className="flex items-center gap-2">
                            <Coins className="w-4 h-4 text-emerald-400" />
                            <span className="font-mono text-xs text-white font-bold tracking-widest uppercase">NODE-81 RECON MODULE</span>
                          </div>
                          <span className="text-[8px] font-mono text-emerald-400 bg-emerald-950/40 px-2 py-0.5 rounded border border-emerald-500/10 font-bold uppercase">FACTUAL PROCESSOR</span>
                        </div>

                        <p className="text-[10px] text-slate-400 uppercase tracking-wider mb-4 leading-relaxed">
                          Enter raw transaction data to verify real-time splits via the live Hermes instance.
                        </p>

                        <div className="space-y-4">
                          <div>
                            <label className="text-[9px] text-slate-500 block mb-1 uppercase tracking-widest font-bold">Raw Gross Sum (£)</label>
                            <input
                              type="number"
                              value={reconcileAmount}
                              onChange={(e) => {
                                const val = Number(e.target.value);
                                setReconcileAmount(val);
                                setReconcileResult(HermesInstance.reconcileRevenue('SecurePayment Direct', val, reconcileMethod));
                              }}
                              className="w-full bg-black border border-slate-800 rounded px-2.5 py-1.5 text-xs text-[#e2e8f0] font-mono focus:outline-none focus:border-cyan-500"
                            />
                          </div>

                          <div>
                            <label className="text-[9px] text-slate-500 block mb-1 uppercase tracking-widest font-bold">Payment Gateway Protocol</label>
                            <div className="grid grid-cols-2 gap-2">
                              {(['CARD', 'BANK_TRANSFER'] as const).map((method) => (
                                <button
                                  key={method}
                                  type="button"
                                  onClick={() => {
                                    setReconcileMethod(method);
                                    setReconcileResult(HermesInstance.reconcileRevenue('SecurePayment Direct', reconcileAmount, method));
                                  }}
                                  className={`text-[9px] font-bold font-mono py-1.5 rounded border transition-all ${
                                    reconcileMethod === method
                                      ? 'bg-emerald-500/20 border-emerald-400 text-emerald-300'
                                      : 'bg-black/80 border-slate-900 text-slate-500 hover:text-slate-300'
                                  }`}
                                >
                                  {method === 'BANK_TRANSFER' ? 'Secure Payment Provider (5% OFF)' : 'CARD GATEWAY'}
                                </button>
                              ))}
                            </div>
                          </div>
                        </div>

                        <div className="h-px bg-slate-900 my-4" />

                        {reconcileResult && (
                          <div className="space-y-2 text-[10px] font-mono bg-black/40 border border-slate-900 rounded p-3">
                            <div className="flex justify-between items-center pb-1 border-b border-slate-900/60 text-slate-400">
                              <span>NexoraX Levi (1%):</span>
                              <span className="text-yellow-400 font-bold">£{reconcileResult.zakat}</span>
                            </div>
                            <div className="flex justify-between items-center pb-1 border-b border-slate-900/60 text-slate-400">
                              <span>10% Maintenance & Hosting Fee (20% Gavel):</span>
                              <span className="text-rose-400 font-bold">£{reconcileResult.gavelFee}</span>
                            </div>
                            <div className="flex justify-between items-center pt-1 text-white font-bold text-xs">
                              <span className="text-emerald-400">Net Corporate Yield:</span>
                              <span className="text-emerald-400">£{Number(reconcileResult.netCorporateYield).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                            </div>
                          </div>
                        )}
                      </div>

                      <div className="mt-4 text-[7.5px] text-slate-600 uppercase tracking-widest text-center">
                        Securely routed &bull; SecurePayment Sort Code: Verified Payment Configuration &bull; Acc: Secure Billing Provider
                      </div>
                    </div>
                  </div>

                  {/* Operational Log Terminal Output */}
                  {(hermesLogs.length > 0 || isSimulating) && (
                    <div className="bg-black/95 border border-slate-800 rounded-xl p-4 font-mono text-xs flex flex-col shadow-inner">
                      <div className="flex items-center justify-between border-b border-slate-900 pb-2 mb-3">
                        <div className="flex items-center gap-2">
                          <Terminal className="w-4 h-4 text-cyan-500 animate-[pulse_2s_infinite]" />
                          <span className="text-white font-bold uppercase tracking-wider text-[10px]">Hermes Shell Console Output</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-ping"></span>
                          <span className="text-[8px] text-slate-400 uppercase">5.161.42.190 DECK ACTIVE</span>
                        </div>
                      </div>

                      {/* Log stream */}
                      <div className="space-y-1.5 max-h-52 overflow-y-auto custom-scrollbar text-[10px] pr-2 mb-4 font-mono text-slate-300 leading-normal">
                        {hermesLogs.map((log, i) => {
                          let isErr = log.includes("[ERROR]");
                          let isSucceed = log.includes("[COMPLETED]") || log.includes("[SUCCESS]") || log.includes("Handshake verified");
                          let isTarget = log.includes("[TARGET DETECTED]");
                          return (
                            <div key={i} className={`font-mono ${isErr ? "text-rose-400" : isSucceed ? "text-emerald-400" : isTarget ? "text-cyan-400" : "text-slate-300"}`}>
                              {log}
                            </div>
                          );
                        })}
                        <div ref={logsEndRef}></div>
                      </div>

                      {/* Micro Dispatch Pipeline Metrics */}
                      {processedProspects.length > 0 && (
                        <div className="border-t border-slate-900 pt-3">
                          <div className="text-[10px] text-slate-500 uppercase font-black mb-2 flex items-center justify-between">
                            <span>Transactional Handshakes Dispatched:</span>
                            <span className="text-cyan-400">{processedProspects.length} / 4 PROSPECTS</span>
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                            {processedProspects.map((p, idx) => (
                              <div key={idx} className="bg-slate-950/60 border border-slate-900 rounded-lg p-2.5 flex items-center justify-between text-[10px]">
                                <div className="space-y-0.5">
                                  <div className="text-white font-bold">{p.name}</div>
                                  <div className="text-slate-500">{p.email}</div>
                                </div>
                                <div className="flex items-center gap-1 px-2 py-0.5 bg-emerald-950/20 text-emerald-300 border border-emerald-500/10 rounded font-bold uppercase tracking-widest text-[8px]">
                                  <Mail size={10} /> DISPATCHED
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Standard Static Grid Below Simulation */}
                  <div className="border border-slate-800 bg-slate-950/15 rounded-xl p-4">
                    <span className="text-[10px] font-mono text-slate-500 uppercase tracking-widest block mb-4">Static Subcategory Nodes:</span>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                      {activeCategory.nodes.map((node, idx) => (
                        <div key={idx} className="border border-white/5 bg-black/40 p-3 rounded-lg flex items-center gap-2">
                          <Hexagon size={12} className="text-slate-500" />
                          <span className="font-mono text-[10px] text-slate-400">{node}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                </div>
              ) : activeCategory.id === 84 ? (
                <div className="space-y-6">
                  <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
                    
                    {/* Engine Parameters Input */}
                    <div className="bg-[#030b14]/90 border border-slate-800 rounded-xl p-5 flex flex-col justify-between shadow-[0_0_40px_rgba(249,115,22,0.05)] relative overflow-hidden group">
                      <div className="absolute top-0 right-0 w-32 h-32 bg-orange-500/5 blur-[50px] pointer-events-none"></div>
                      
                      <div>
                        <div className="flex items-center justify-between mb-4 pb-2 border-b border-slate-900">
                          <div className="flex items-center gap-2">
                            <Cpu className="w-4 h-4 text-orange-400" />
                            <span className="font-mono text-xs text-white font-bold tracking-widest">NEXORAX ENGINE REGULATION</span>
                          </div>
                          <span className="px-2 py-0.5 rounded text-[8px] font-mono text-orange-300 bg-orange-950/30 border border-orange-500/20 font-bold uppercase">Node-84 Core</span>
                        </div>
                        
                        <p className="text-xs text-slate-400 leading-relaxed mb-6">
                          Active logical governance. Calculates complex VAT-compliant invoice distribution, extracting exactly 1% Perpetual 3% Community Allocation (distributed in equal 33.33% splits to local &amp; global structures) and 10% Maintenance & Hosting Fee prior to routing direct settlement net yields.
                        </p>

                        <div className="space-y-4 mb-6">
                          <div>
                            <label className="text-[9px] text-slate-500 block mb-1 uppercase tracking-widest font-bold">Net Base Invoice Amount (£)</label>
                            <div className="relative">
                              <span className="absolute left-3 top-2.5 text-xs text-slate-500 font-mono">£</span>
                              <input
                                type="number"
                                value={engineInput}
                                onChange={(e) => setEngineInput(Number(e.target.value))}
                                className="w-full bg-black/60 border border-slate-800 rounded pl-7 pr-3 py-2 text-xs text-[#e2e8f0] font-mono focus:outline-none focus:border-orange-500"
                              />
                            </div>
                          </div>

                          <div className="grid grid-cols-2 gap-3 text-[10px]">
                            <div className="bg-black/40 border border-slate-900 rounded p-2.5">
                              <span className="text-slate-500 block text-[8px] uppercase font-bold tracking-widest mb-1">VAT Amount (20%)</span>
                              <span className="text-orange-400 font-bold">£{vatAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                            </div>
                            <div className="bg-black/40 border border-slate-900 rounded p-2.5">
                              <span className="text-slate-500 block text-[8px] uppercase font-bold tracking-widest mb-1">Gross Invoice Due</span>
                              <span className="text-white font-bold">£{grossAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                            </div>
                          </div>

                          <div className="grid grid-cols-2 gap-3 text-[10px]">
                            <div className="bg-black/40 border border-slate-900 rounded p-2.5">
                              <span className="text-slate-500 block text-[8px] uppercase font-bold tracking-widest mb-1">Levy Split (1%)</span>
                              <span className="text-yellow-400 font-bold">£{levyAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                            </div>
                            <div className="bg-black/40 border border-slate-900 rounded p-2.5">
                              <span className="text-slate-500 block text-[8px] uppercase font-bold tracking-widest mb-1">10% Maintenance & Hosting Fee (20%)</span>
                              <span className="text-rose-400 font-bold">£{lifetimeTax.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="space-y-3">
                        <button
                          onClick={executeEngineReconciliation}
                          disabled={engineLoading}
                          className={`w-full py-3 rounded-lg font-mono text-xs uppercase tracking-widest font-bold flex items-center justify-center gap-2 border transition-all ${
                            engineLoading
                              ? "bg-slate-950/40 border-slate-800 text-slate-600 cursor-not-allowed"
                              : "bg-gradient-to-r from-orange-700 to-orange-950 hover:from-orange-600 hover:to-orange-900 text-white border-orange-500/30 hover:shadow-[0_0_20px_rgba(249,115,22,0.35)] cursor-pointer"
                          }`}
                        >
                          <Cpu className="w-4 h-4 animate-pulse" /> {engineLoading ? "ENGINE RUNNING..." : "COMPILE MASTER RECONCILIATION"}
                        </button>
                      </div>
                    </div>

                    {/* Engine Output Ledger / SecurePayment Target */}
                    <div className="bg-[#010409] border border-slate-800 rounded-xl p-5 flex flex-col justify-between shadow-2xl relative">
                      <div className="absolute top-0 right-0 w-24 h-24 bg-orange-500/5 blur-3xl pointer-events-none"></div>

                      <div>
                        <div className="flex items-center justify-between pb-2 border-b border-slate-900 mb-4">
                          <div className="flex items-center gap-2">
                            <Server className="w-4 h-4 text-orange-400" />
                            <span className="font-mono text-xs text-white font-bold tracking-widest uppercase">REAL-TIME SecurePayment SETTLEMENT NODE</span>
                          </div>
                          <span className="text-[8px] font-mono text-orange-400 bg-orange-950/40 px-2 py-0.5 rounded border border-orange-500/10 font-bold uppercase">SECURE BANKING ROUTER</span>
                        </div>

                        <div className="space-y-3.5">
                          <div className="bg-black/60 border border-slate-900 p-3 rounded space-y-1">
                            <span className="text-[8px] text-slate-500 font-mono uppercase tracking-widest">AUTHORIZED INTERMEDIARY</span>
                            <div className="text-xs font-mono font-bold text-white flex justify-between">
                              <span>SecurePayment FINANCIAL ROUTER</span>
                              <span className="text-emerald-400">STATUS: ON-LINE</span>
                            </div>
                            <div className="text-[10px] text-slate-400 font-mono">
                              Sort Code: <span className="text-white">Verified Payment Configuration</span> | Account No: <span className="text-white">Secure Billing Provider</span>
                            </div>
                          </div>

                          <div className="space-y-2 text-[10px] font-mono bg-black/40 border border-slate-900 rounded p-3">
                            <div className="flex justify-between items-center pb-1.5 border-b border-slate-900/60 text-slate-400">
                              <span className="flex items-center gap-1">
                                <span className="w-1 h-1 rounded-full bg-yellow-400"></span> 
                                Local Escrow (Dunfermline) Zakat (33.33%):
                              </span>
                              <span className="text-white font-bold">£{charity1Split.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                            </div>
                            <div className="flex justify-between items-center pb-1.5 border-b border-slate-900/60 text-slate-400">
                              <span className="flex items-center gap-1">
                                <span className="w-1 h-1 rounded-full bg-yellow-400"></span> 
                                Global Relief Fund Zakat (33.33%):
                              </span>
                              <span className="text-white font-bold">£{charity2Split.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                            </div>
                            <div className="flex justify-between items-center pb-1.5 border-b border-slate-900/60 text-slate-400">
                              <span className="flex items-center gap-1">
                                <span className="w-1 h-1 rounded-full bg-yellow-400"></span> 
                                UK Structural Projects Zakat (33.33%):
                              </span>
                              <span className="text-white font-bold">£{charity3Split.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                            </div>
                            <div className="flex justify-between items-center pt-2 text-white font-bold text-xs">
                              <span className="text-emerald-400 flex items-center gap-1">
                                <CheckCircle2 size={12} className="text-emerald-400" /> Net Yield to finance:
                              </span>
                              <span className="text-emerald-400">£{netCorporateYield.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="mt-4 text-[7.5px] text-slate-600 uppercase tracking-widest text-center border-t border-slate-900 pt-3">
                        LEVY ARCHITECTURE STRICTLY DEPLOYED UNDER ACTIVE 10% Maintenance & Hosting Fee ENFORCEMENT PROTOCOL
                      </div>
                    </div>
                  </div>

                  {/* Engine Console Output Logs */}
                  {(engineLogs.length > 0 || engineLoading) && (
                    <div className="bg-black/95 border border-slate-800 rounded-xl p-4 font-mono text-xs flex flex-col shadow-inner">
                      <div className="flex items-center justify-between border-b border-slate-900 pb-2 mb-3">
                        <div className="flex items-center gap-2">
                          <Terminal className="w-4 h-4 text-orange-400 animate-[pulse_2s_infinite]" />
                          <span className="text-white font-bold uppercase tracking-wider text-[10px]">NexoraX Engine Console</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="w-1.5 h-1.5 rounded-full bg-orange-500 animate-ping"></span>
                          <span className="text-[8px] text-slate-400 uppercase">0.8s SLA RESOLVABLE</span>
                        </div>
                      </div>

                      {/* Log stream */}
                      <div className="space-y-1.5 max-h-52 overflow-y-auto custom-scrollbar text-[10px] pr-2 font-mono text-slate-300 leading-normal">
                        {engineLogs.map((log, i) => (
                          <div key={i} className={`font-mono ${log.includes("[COMPLETED]") ? "text-emerald-400" : log.includes("[CALCULATION]") ? "text-cyan-400" : log.includes("[TAXATION]") ? "text-orange-200" : "text-slate-300"}`}>
                            {log}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Operational Nodes Listing */}
                  <div className="border border-slate-800 bg-slate-950/15 rounded-xl p-4">
                    <span className="text-[10px] font-mono text-slate-500 uppercase tracking-widest block mb-4">Static Subcategory Nodes (100 total):</span>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                      {activeCategory.nodes.slice(0, 15).map((node, idx) => (
                        <div key={idx} className="border border-white/5 bg-black/40 p-3 rounded-lg flex items-center gap-2">
                          <Hexagon size={12} className="text-orange-500/50" />
                          <span className="font-mono text-[10px] text-slate-400">{node}</span>
                        </div>
                      ))}
                      <div className="border border-dashed border-slate-800 bg-transparent p-3 rounded-lg flex items-center justify-center">
                        <span className="font-mono text-[9px] text-slate-600 block uppercase tracking-wider font-bold">and 85 more integrated nodes</span>
                      </div>
                    </div>
                  </div>
                </div>
              ) : activeCategory.id === 85 ? (
                <div className="space-y-6">
                  <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
                    
                    {/* Media Forge Selection & Parameterizing */}
                    <div className="bg-[#030b14]/90 border border-slate-800 rounded-xl p-5 flex flex-col justify-between shadow-[0_0_40px_rgba(232,64,243,0.05)] relative overflow-hidden group">
                      <div className="absolute top-0 right-0 w-32 h-32 bg-fuchsia-500/5 blur-[50px] pointer-events-none"></div>
                      
                      <div>
                        <div className="flex items-center justify-between mb-4 pb-2 border-b border-slate-900">
                          <div className="flex items-center gap-2">
                            <Sparkles className="w-4 h-4 text-fuchsia-400" />
                            <span className="font-mono text-xs text-white font-bold tracking-widest">COGNITIVE MEDIA FORGE (NODE-85)</span>
                          </div>
                          <span className="px-2 py-0.5 rounded text-[8px] font-mono text-fuchsia-300 bg-fuchsia-950/30 border border-fuchsia-500/20 font-bold uppercase">Gen-Core V2</span>
                        </div>
                        
                        <p className="text-xs text-slate-400 leading-relaxed mb-6">
                          Autonomous production. Direct creative asset synthesis, memetic stream formulation, deepfake digital identity forge, and subconscious aesthetic injection optimized globally to match Director's Services Guide.
                        </p>

                        <div className="space-y-4 mb-6">
                          <div>
                            <label className="text-[10px] text-slate-500 block mb-1.5 uppercase tracking-widest font-bold">Select Active Sub-Node Processor</label>
                            <select
                              value={selectedGeneratorNode}
                              onChange={(e) => {
                                setSelectedGeneratorNode(e.target.value);
                                setGeneratedResult(null);
                              }}
                              className="w-full bg-[#02050b] border border-slate-850 rounded px-2.5 py-2 text-xs text-[#e2e8f0] font-mono focus:outline-none focus:ring-1 focus:ring-fuchsia-500 block cursor-pointer"
                            >
                              {activeCategory.nodes.slice(0, 15).map((node, i) => (
                                <option key={i} value={node} className="bg-[#05050d] text-slate-300 uppercase tracking-wider font-mono">
                                  {node.toUpperCase()}
                                </option>
                              ))}
                            </select>
                          </div>

                          <div>
                            <label className="text-[10px] text-slate-500 block mb-1.5 uppercase tracking-widest font-bold">Custom Creative Directive Prompt (Optional)</label>
                            <textarea
                              value={generatorPrompt}
                              onChange={(e) => setGeneratorPrompt(e.target.value)}
                              placeholder="e.g. Synthesize premium black &amp; white interface mockup for SecurePayment settlement billing..."
                              maxLength={200}
                              rows={2}
                              className="w-full bg-black/60 border border-slate-800 rounded px-3 py-2 text-xs text-[#e2e8f0] font-mono focus:outline-none focus:border-fuchsia-500 resize-none"
                            />
                            <div className="text-[9px] text-right text-slate-600 mt-1 uppercase">MAX 200 SYMBOLS</div>
                          </div>
                        </div>
                      </div>

                      <div className="space-y-3">
                        <button
                          onClick={executeGeneratorAssetForge}
                          disabled={generatorLoading}
                          className={`w-full py-3 rounded-lg font-mono text-xs uppercase tracking-widest font-bold flex items-center justify-center gap-2 border transition-all ${
                            generatorLoading
                              ? "bg-slate-950/40 border-slate-800 text-slate-600 cursor-not-allowed"
                              : "bg-gradient-to-r from-fuchsia-700 to-fuchsia-950 hover:from-fuchsia-600 hover:to-fuchsia-900 text-white border-fuchsia-500/30 hover:shadow-[0_0_20px_rgba(232,64,243,0.35)] cursor-pointer"
                          }`}
                        >
                          <Sparkles className="w-4 h-4 animate-spin" /> {generatorLoading ? "SYNTHESIZING..." : "FORGE CREATIVE MEDIAS"}
                        </button>
                      </div>
                    </div>

                    {/* Generator Dynamic Preview Output */}
                    <div className="bg-[#010409]/95 border border-slate-800 rounded-xl p-5 flex flex-col justify-between shadow-2xl relative min-h-[300px]">
                      <div className="absolute top-0 right-0 w-24 h-24 bg-fuchsia-500/5 blur-3xl pointer-events-none"></div>

                      <div>
                        <div className="flex items-center justify-between pb-2 border-b border-slate-900 mb-4">
                          <div className="flex items-center gap-2">
                            <Server className="w-4 h-4 text-fuchsia-400" />
                            <span className="font-mono text-xs text-white font-bold tracking-widest uppercase">PREVIEW GENERATOR RENDERING</span>
                          </div>
                          <span className="text-[8px] font-mono text-fuchsia-400 bg-[#e840f3]/10 px-2 py-0.5 rounded border border-[#e840f3]/10 font-bold uppercase">LIVE OUTPUT</span>
                        </div>

                        {generatorLoading ? (
                          <div className="h-44 flex flex-col items-center justify-center gap-3">
                            <div className="w-8 h-8 rounded-full border-2 border-fuchsia-500 border-t-transparent animate-spin"></div>
                            <span className="font-mono text-[10px] text-fuchsia-300 animate-pulse uppercase tracking-widest">NexoraX Asset synthesis operating...</span>
                          </div>
                        ) : generatedResult ? (
                          <div className="space-y-4">
                            <div className="text-[10px] font-mono bg-[#e840f3]/5 border border-[#e840f3]/25 p-3 rounded">
                              <span className="text-[9px] text-fuchsia-400 block font-bold uppercase tracking-wider mb-2">SYNTHESIS SUCCESSFUL:</span>
                              <p className="text-slate-300 mb-3">{generatedResult.description}</p>
                              {generatedResult.text && (
                                <div className="bg-black/60 border border-slate-900 p-2.5 rounded font-mono text-[9px] text-emerald-300 whitespace-pre-wrap leading-relaxed shadow-inner">
                                  {generatedResult.text}
                                </div>
                              )}
                            </div>

                            <div className="bg-gradient-to-r from-fuchsia-950/20 to-indigo-950/20 border border-slate-900 p-2.5 rounded text-[8px] font-mono uppercase text-slate-500 tracking-wider">
                              File System Signature: <span className="text-white">gdrive:Nexorax_Ecosystem_Backups/Factual_Generations/Node-85-{selectedGeneratorNode.replace(/\s+/g, '_')}.log</span>
                            </div>
                          </div>
                        ) : (
                          <div className="h-44 flex flex-col items-center justify-center text-center text-slate-500 font-mono text-[10px] uppercase p-4 border border-dashed border-slate-850 rounded">
                            <Sparkles className="w-6 h-6 text-slate-600 mb-2" />
                            <span>Select active node or prompt &amp; execute creative swarm forge to output physical artifacts.</span>
                          </div>
                        )}
                      </div>

                      <div className="mt-4 text-[7.5px] text-slate-600 uppercase tracking-widest text-center border-t border-slate-900 pt-3">
                        CloudSync-02 Verified Output &bull; Compliant with Global Regulatory Corridor Standards
                      </div>
                    </div>
                  </div>

                  {/* Operational Nodes Listing */}
                  <div className="border border-slate-800 bg-slate-950/15 rounded-xl p-4">
                    <span className="text-[10px] font-mono text-slate-500 uppercase tracking-widest block mb-4">Static Subcategory Nodes (100 total):</span>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                      {activeCategory.nodes.slice(0, 15).map((node, idx) => (
                        <div key={idx} className="border border-white/5 bg-black/40 p-3 rounded-lg flex items-center gap-2">
                          <Hexagon size={12} className="text-fuchsia-500/50" />
                          <span className="font-mono text-[10px] text-slate-400">{node}</span>
                        </div>
                      ))}
                      <div className="border border-dashed border-slate-800 bg-transparent p-3 rounded-lg flex items-center justify-center">
                        <span className="font-mono text-[9px] text-slate-600 block uppercase tracking-wider font-bold">and 85 more integrated nodes</span>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                /* STANDARD GRID FOR ALL OTHER CATEGORIES */
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 auto-rows-max">
                  {activeCategory.nodes.map((node, idx) => (
                    <motion.div
                      key={`${activeCategory.id}-${idx}`}
                      initial={{ opacity: 0, scale: 0.95 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: idx * 0.05 + 0.1, duration: 0.3 }}
                      className="group border border-white/5 bg-black/40 p-5 rounded-lg hover:border-cyan-500/40 hover:bg-cyan-900/10 transition-all duration-300 relative overflow-hidden"
                    >
                      <div className="absolute top-0 left-0 w-1 h-full bg-gradient-to-b from-transparent via-cyan-500/0 to-transparent group-hover:via-cyan-500/50 transition-all"></div>
                      <div className="flex items-start gap-4">
                        <div className="mt-0.5 text-cyan-600 group-hover:text-cyan-400 transition-colors">
                          <Hexagon size={16} />
                        </div>
                        <div>
                          <h4 className="font-mono text-sm text-slate-300 group-hover:text-white transition-colors">
                            {node}
                          </h4>
                          <p className="text-[10px] text-slate-600 mt-1 uppercase tracking-widest font-mono group-hover:text-cyan-600 transition-colors">
                            Status: Active // Operational
                          </p>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              )}

              <div className="mt-auto pt-8 flex items-center justify-between text-xs font-mono text-slate-600">
                <span>Total Nodes: {activeCategory.nodes.length}</span>
                <span className="flex items-center gap-2"><Component size={12}/> DATA_SYNC_ENABLED</span>
              </div>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>
    </div>
  );
}
